import type { PublicProfile } from "@bing/shared";
import { useEffect, useState, type ReactNode } from "react";
import { Avatar } from "../components/Avatar";
import { MatchList, PerGameStats, StatTotals } from "../components/GameStats";
import { PageShell } from "../components/PageShell";
import { displayName } from "../components/profile-ui";
import { ReportSheet } from "../components/ReportSheet";
import { OfficialBadge, Spinner } from "../components/social-ui";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";

/** «این بخش خصوصی است» — a hidden stats/history section (the owner made it private). */
function PrivateSection(): ReactNode {
  return (
    <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-outline-variant/60 bg-surface-container-low px-md py-lg text-center font-body-md text-body-md text-on-surface-variant">
      <span aria-hidden="true">🔒</span>
      {fa.profileView.private}
    </div>
  );
}

/**
 * Another user's profile view (opened from the friends list, search, or a table roster).
 * Public identity (avatar + name + official badge + @handle) always shows; stats/trophies
 * and recent history show UNLESS the owner hid them — the server omits hidden sections and
 * flags them, and we render «این بخش خصوصی است». Reuses the shared stats/history + shell
 * presentation so it stays consistent with the own-profile screen. RTL, theme-aware.
 */
export function ProfileView(): ReactNode {
  const viewedUserId = useSession((s) => s.viewedUserId);
  const profileReturnView = useSession((s) => s.profileReturnView);
  const setView = useSession((s) => s.setView);
  const api = useSession((s) => s.api);
  const openChat = useSession((s) => s.openChat);
  const friends = useSession((s) => s.friends);
  // Only an ACCEPTED friend can be messaged — the DM room authorises on the friendship, so
  // offering «پیام» to a stranger would be a button that fails. Read from the store's live list
  // rather than the public profile (which carries no relationship).
  const friend = friends.find((f) => f.userId === viewedUserId && f.status === "accepted") ?? null;

  const [data, setData] = useState<PublicProfile | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!viewedUserId) return;
    let cancelled = false;
    setPhase("loading");
    api
      .getUserProfile(viewedUserId)
      .then((p) => {
        if (cancelled) return;
        setData(p);
        setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) setPhase("error");
      });
    return () => {
      cancelled = true;
    };
  }, [viewedUserId, api]);

  const back = (): void => setView(profileReturnView);

  return (
    <PageShell title={fa.profileView.title} icon="profile" onBack={back}>
      {phase === "loading" ? (
        <div className="flex flex-col items-center gap-sm py-2xl text-on-surface-variant">
          <Spinner />
          <span className="font-body-md text-body-md">{fa.profileView.loading}</span>
        </div>
      ) : phase === "error" || !data ? (
        <div className="flex flex-col items-center gap-md py-2xl text-center">
          <p className="m-0 font-body-md text-body-md text-on-surface-variant">
            {fa.profileView.loadError}
          </p>
          <button
            type="button"
            onClick={() => setView(profileReturnView)}
            className="appearance-none cursor-pointer rounded-lg border border-outline-variant bg-surface-container-high px-lg py-sm font-label-md text-label-md text-on-surface transition-colors hover:bg-surface-variant"
          >
            {fa.common.back}
          </button>
        </div>
      ) : (
        <>
          {/* Hero: avatar + name (+ official badge) + @handle — always visible. */}
          <section className="flex flex-col items-center gap-sm pt-sm">
            <Avatar
              avatarId={data.user.avatarId}
              avatarPhotoId={data.user.avatarPhotoId}
              size={104}
              ringClass="border-secondary/70"
              ringWidth={2}
            />
            <div className="flex w-full items-center justify-center gap-1.5">
              {/* ⚠️ `dir="auto"` — a Latin name inside this RTL shell is its own run, and an RTL
                  block resolves a trailing neutral against the paragraph direction (the measured
                  «Latin names clipped from the front» shape). `break-words` because a long
                  single-token name has no break opportunity and would otherwise push the row
                  wider than a 320 px screen. */}
              {/* ⏭ THIS HEADING HAS NO SIZE TOKEN, AND HAS NOT HAD ONE SINCE IT WAS WRITTEN. It
                  carried the `headline-lg` token as a font+text pair; it is in NEITHER scale in
                  `tailwind.config.js` (the scale holds `headline-md` 22px and `headline-sm` 18px),
                  so both classes generated no rule at all and the name has always rendered at the
                  INHERITED size. The dead classes are gone — that is a no-op by construction, and
                  the screen is byte-for-byte what it was — but giving it a real heading size would
                  CHANGE what ships, so it is left for the operator rather than taken here.
                  Found by `type-scale-tokens.test.ts`, which is why that sweep exists. */}
              <h2 dir="auto" className="m-0 min-w-0 break-words text-center text-on-surface">
                {displayName(data.user)}
              </h2>
              {data.user.isOfficial ? <OfficialBadge size={20} /> : null}
            </div>
            {data.user.appUsername ? (
              <p className="m-0 font-body-md text-body-md text-on-surface-variant" dir="ltr">
                @{data.user.appUsername}
              </p>
            ) : null}
            <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
              {/* «پیام» — the profile was a DEAD END for starting a conversation: the compose
                  button sent you to the friends list, a friend row sent you HERE, and here had no
                  way to reach a chat. A friend can now be messaged from the screen you land on. */}
              {friend ? (
                <button
                  type="button"
                  onClick={() => openChat(friend)}
                  className="flex cursor-pointer appearance-none items-center gap-1 rounded-full border-0 bg-primary-container px-4 py-1.5 font-caption text-caption font-semibold text-on-primary-container shadow-sm transition-transform active:scale-95"
                >
                  💬 {fa.chat.messageAction}
                </button>
              ) : null}
              {/* Report this user (Part D2 — mini-app entry point). */}
              <button
                type="button"
                onClick={() => setShowReport(true)}
                className="flex cursor-pointer appearance-none items-center gap-1 rounded-full border border-outline-variant/60 bg-surface-container-high px-3 py-1 font-caption text-caption text-on-surface-variant transition-colors hover:border-error/50 hover:text-error"
              >
                🚩 {fa.report.action}
              </button>
            </div>
          </section>

          {/* Stats + trophies. */}
          <section className="flex flex-col gap-md">
            <h3 className="m-0 px-sm font-headline-sm text-headline-sm text-on-surface">
              {fa.profileView.stats}
            </h3>
            {data.statsHidden || !data.stats ? (
              <PrivateSection />
            ) : (
              <>
                <StatTotals stats={data.stats} />
                <PerGameStats stats={data.stats} />
              </>
            )}
          </section>

          {/* Recent match history. */}
          <section className="flex flex-col gap-sm">
            <h3 className="m-0 px-sm font-headline-sm text-headline-sm text-on-surface">
              {fa.profileView.history}
            </h3>
            {data.historyHidden || !data.history ? (
              <PrivateSection />
            ) : data.history.items.length === 0 ? (
              <div className="rounded-xl border border-dashed border-outline-variant/50 p-lg text-center font-body-md text-body-md text-on-surface-variant">
                {fa.profileView.noMatches}
              </div>
            ) : (
              <MatchList items={data.history.items} />
            )}
          </section>
          {showReport && viewedUserId ? (
            <ReportSheet
              name={displayName(data.user)}
              onSubmit={(input) => api.reportUser(viewedUserId, input).then(() => undefined)}
              onUpload={(blob, mime, onProgress) =>
                api.uploadReportAttachment(blob, mime, onProgress)
              }
              onClose={() => setShowReport(false)}
            />
          ) : null}
        </>
      )}
    </PageShell>
  );
}
